import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { EmmaPath } from '@/router/paths'

import { MUTATE_KEY_CREATE_CUSTOMER, QUERY_KEY_CUSTOMER } from '../../types/constants'
import { Customer } from '../../types/customer'
import { authenticatedApi } from '../../utils/api'

async function createCustomer({ customer }: { customer: Customer }) {
  return authenticatedApi.post(`customer`, { json: customer }).json()
}

export function useCreateCustomer() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationKey: [MUTATE_KEY_CREATE_CUSTOMER],
    mutationFn: ({ customer }: { customer: Customer }) => createCustomer({ customer }),

    onMutate: async () => {
      notifications.show({ title: 'Creating Customer', message: 'Creating Customer' })
    },

    onSuccess: async (data: any) => {
      // Set the individual customer data (no queryFn needed since we have the data)
      queryClient.setQueryData([QUERY_KEY_CUSTOMER, data?._id], data)

      notifications.show({ title: 'Customer', message: 'Customer has been created successfully' })

      // Navigate first, then invalidate queries in background
      if (data?._id) {
        navigate(EmmaPath.CUSTOMER_PAGE.replace(':id', data?._id))
      }

      // Invalidate list queries so new customer appears in search results
      // This runs after navigation so it doesn't block the UI
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_CUSTOMER],
        predicate: (query) => {
          // Only invalidate queries that are NOT the individual customer we just created
          const isIndividualCustomer = query.queryKey.length === 2 && query.queryKey[1] === data?._id
          return !isIndividualCustomer
        },
      })
    },

    onError: () => {
      notifications.show({
        title: 'Customer failed',
        message: 'Failed to create the customer',
        color: 'red',
      })
    },
  })
}
